import React from 'react';

// Format helpers
const formatNumber = (amount) => {
  return new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
};

const formatDateLong = (dateString) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const TYPE_LABELS = { invoice: 'Invoice', quote: 'Quotation', sj: 'Surat Jalan', po: 'Purchase Order' };

/**
 * Pre-render the status badge as a data-URL PNG.
 * html2canvas reliably copies images, but mis-handles vertical centering of inline text,
 * so we bake the badge to an image to guarantee pixel-perfect output.
 */
const renderBadgeDataUrl = (label, bg) => {
  if (typeof document === 'undefined') return '';
  const dpr = 2;
  const padX = 14;
  const fontSize = 11;
  const height = 22;
  const fontFamily = "700 11px 'Plus Jakarta Sans', 'Arial', sans-serif";
  // Measure text
  const measureCanvas = document.createElement('canvas');
  const mctx = measureCanvas.getContext('2d');
  mctx.font = fontFamily;
  const textWidth = mctx.measureText(label).width;
  const width = Math.max(58, Math.ceil(textWidth + padX * 2 + label.length * 0.5));
  const canvas = document.createElement('canvas');
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  // Background rounded rect
  const radius = 4;
  ctx.fillStyle = bg;
  ctx.beginPath();
  ctx.moveTo(radius, 0);
  ctx.lineTo(width - radius, 0);
  ctx.quadraticCurveTo(width, 0, width, radius);
  ctx.lineTo(width, height - radius);
  ctx.quadraticCurveTo(width, height, width - radius, height);
  ctx.lineTo(radius, height);
  ctx.quadraticCurveTo(0, height, 0, height - radius);
  ctx.lineTo(0, radius);
  ctx.quadraticCurveTo(0, 0, radius, 0);
  ctx.closePath();
  ctx.fill();
  // Text — letterSpacing applied manually
  ctx.fillStyle = '#FFFFFF';
  ctx.font = fontFamily;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText(label, width / 2, height / 2 + 0.5);
  return { url: canvas.toDataURL('image/png'), width, height };
};
const getStatusInfo = (transaction) => {
  const type = transaction.type || 'invoice';
  const st = (transaction.status || 'draft').toLowerCase();

  if (type === 'invoice') {
    if (st === 'paid') return { label: 'Paid', bg: '#10B981' };
    if (st === 'cancelled') return { label: 'Cancelled', bg: '#64748B' };
    return { label: 'Unpaid', bg: '#E53935' };
  }
  if (type === 'quote') {
    if (st === 'converted') return { label: 'Converted', bg: '#10B981' };
    if (st === 'cancelled') return { label: 'Cancelled', bg: '#64748B' };
    if (st === 'sent') return { label: 'Sent', bg: '#0EA5E9' };
    return { label: 'Draft', bg: '#00A6B7' };
  }
  if (type === 'po') {
    if (st === 'received') return { label: 'Received', bg: '#10B981' };
    if (st === 'cancelled') return { label: 'Cancelled', bg: '#64748B' };
    if (st === 'ordered') return { label: 'Ordered', bg: '#0EA5E9' };
    return { label: 'Draft', bg: '#F59E0B' };
  }
  // sj
  if (st === 'cancelled') return { label: 'Cancelled', bg: '#64748B' };
  if (st === 'sent') return { label: 'Delivered', bg: '#10B981' };
  return { label: 'Pending', bg: '#F59E0B' };
};

/**
 * Universal document template (Invoice / Quotation / Surat Jalan).
 * Layout matches uploaded INV#00027.pdf sample.
 * - Invoice & Quotation: full table with prices + totals.
 *   Invoice extra: Payment Details footer + Total Due.
 *   Quotation: Validity / Terms only (no Payment Details, no Total Due label).
 * - Surat Jalan: no prices, checklist column, Penerima/Pengirim signature area.
 */
const InvoiceTemplate = React.forwardRef(({ transaction, settings, products = [], hideStatus = false, htmlId = 'invoice-template' }, ref) => {
  if (!transaction) return null;
  const type = transaction.type || 'invoice';
  const typeLabel = TYPE_LABELS[type] || 'Document';
  const isInvoice = type === 'invoice';
  const isQuote = type === 'quote';
  const isSJ = type === 'sj';
  const isPO = type === 'po';

  // Compute totals (for invoice + quote + PO)
  const subTotal = (transaction.items || []).reduce((s, it) => s + (Number(it.total) || 0), 0);
  const total = Number(transaction.total != null ? transaction.total : subTotal);
  const totalDue = transaction.status === 'paid' ? 0 : total;

  const statusInfo = getStatusInfo(transaction);

  const customerEmail = transaction.customerEmail || '';
  const customerAddress = transaction.customerAddress || '';

  // Number / date labels per type
  const docNumberLabel = isQuote ? 'Quotation Number' : isSJ ? 'Surat Jalan Number' : isPO ? 'PO Number' : 'Invoice Number';
  const docDateLabel = isQuote ? 'Quotation Date' : isSJ ? 'Delivery Date' : isPO ? 'PO Date' : 'Invoice Date';
  const showDueDate = isInvoice && transaction.dueDate;
  const showValidUntil = isQuote && transaction.dueDate;

  return (
    <div
      ref={ref}
      id={htmlId}
      data-testid="invoice-template"
      style={{
        width: '794px', // ~210mm at 96dpi
        background: '#FFFFFF',
        color: '#1F2937',
        fontFamily: "'Plus Jakarta Sans', Arial, sans-serif",
        padding: '48px 56px',
        boxSizing: 'border-box',
        fontSize: '12px',
        lineHeight: 1.5,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
        {/* Left: Logo + Company */}
        <div style={{ maxWidth: '55%' }}>
          {settings?.logoImage ? (
            <img
              src={settings.logoImage}
              alt="Logo"
              crossOrigin="anonymous"
              style={{ maxHeight: '70px', maxWidth: '240px', objectFit: 'contain', marginBottom: '12px', display: 'block' }}
            />
          ) : (
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#1F2937', marginBottom: '12px' }}>
              {settings?.companyName || 'InvoicePro'}
            </div>
          )}
          <div style={{ fontSize: '11px', color: '#374151', lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600, color: '#111827' }}>{settings?.companyName || ''}</div>
            {settings?.email && <div>{settings.email}</div>}
            {settings?.phone && <div>{settings.phone}</div>}
            {settings?.address && (
              <div style={{ marginTop: '2px', whiteSpace: 'pre-line' }}>
                {settings.address}
              </div>
            )}
            {(settings?.city || settings?.state || settings?.zip) && (
              <div>
                {[settings?.city, settings?.state].filter(Boolean).join(', ')}
                {settings?.zip ? ` ${settings.zip}` : ''}
              </div>
            )}
          </div>
        </div>
        {/* Right: Doc title + meta + status */}
        <div style={{ textAlign: 'right' }}>
          <div
            style={{
              fontSize: '36px',
              fontWeight: 700,
              letterSpacing: '-0.5px',
              color: '#111827',
              lineHeight: 1,
              // Saat badge status disembunyikan (mis. PDF email quote), tambahkan
              // margin ekstra supaya title tidak menempel dengan metadata di bawahnya.
              marginBottom: hideStatus ? '32px' : '14px',
            }}
          >
            {typeLabel}
          </div>
          {/* Status badge — pre-baked as PNG image so html2canvas reproduces it pixel-perfect */}
          {!hideStatus && (() => {
            const baked = renderBadgeDataUrl(statusInfo.label, statusInfo.bg);
            if (!baked) return null;
            return (
              <img
                src={baked.url}
                alt={statusInfo.label}
                width={baked.width}
                height={baked.height}
                style={{ display: 'block', marginLeft: 'auto', marginBottom: '14px' }}
              />
            );
          })()}
          <div style={{ fontSize: '11px', color: '#374151', lineHeight: 1.8 }}>
            <div>
              <span style={{ fontWeight: 700 }}>{docNumberLabel}: </span>
              <span>{transaction.id}</span>
            </div>
            <div>
              <span style={{ fontWeight: 700 }}>{docDateLabel}: </span>
              <span>{formatDateLong(transaction.date)}</span>
            </div>
            {showDueDate && (
              <div>
                <span style={{ fontWeight: 700 }}>Due Date: </span>
                <span>{formatDateLong(transaction.dueDate)}</span>
              </div>
            )}
            {showValidUntil && (
              <div>
                <span style={{ fontWeight: 700 }}>Valid Until: </span>
                <span>{formatDateLong(transaction.dueDate)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Billed From / Billed To (Sent From / Sent To for SJ, Ordered From for PO) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '40px', marginBottom: '28px', paddingTop: '16px', borderTop: '1px solid #E5E7EB' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>
            {isSJ ? 'Sent From' : isPO ? 'Ordered By' : 'Billed From'}
          </div>
          <div style={{ fontSize: '11px', color: '#374151', lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600 }}>{settings?.companyName || ''}</div>
            {settings?.email && <div>{settings.email}</div>}
            {settings?.phone && <div>{settings.phone}</div>}
          </div>
        </div>
        <div style={{ flex: 1, textAlign: 'right' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>
            {isSJ ? 'Delivered To' : isPO ? 'Supplier' : 'Billed To'}
          </div>
          <div style={{ fontSize: '11px', color: '#374151', lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600 }}>{transaction.customerName || ''}</div>
            {customerEmail && <div>{customerEmail}</div>}
            {customerAddress && <div style={{ whiteSpace: 'pre-line' }}>{customerAddress}</div>}
          </div>
        </div>
      </div>

      {/* Items Table */}
      {isSJ ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px', fontSize: '11px' }}>
          <thead>
            <tr style={{ background: '#F3F4F6' }}>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#111827', borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB', width: '50px' }}>NO</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#111827', borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB' }}>DESCRIPTION</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#111827', borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB', width: '120px' }}>QUANTITY</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#111827', borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB', width: '100px' }}>CHECK LIST</th>
            </tr>
          </thead>
          <tbody>
            {(transaction.items || []).map((item, idx) => {
              const product = products.find((p) => p.id === item.productId);
              const description = product?.description || '';
              return (
                <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6', verticalAlign: 'top' }}>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#6B7280' }}>{idx + 1}</td>
                  <td style={{ padding: '12px', color: '#1F2937' }}>
                    <div style={{ fontWeight: 600 }}>{item.name}</div>
                    {description && (
                      <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '4px', whiteSpace: 'pre-line' }}>
                        {description}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {item.qty} {item.unit || ''}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <div style={{ width: '18px', height: '18px', border: '1.5px solid #9CA3AF', borderRadius: '3px', display: 'inline-block' }} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px', fontSize: '11px' }}>
          <thead>
            <tr style={{ background: '#F3F4F6' }}>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#111827', borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB', width: '40px' }}>NO</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#111827', borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB' }}>DESCRIPTION</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#111827', borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB', width: '90px' }}>QUANTITY</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#111827', borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB', width: '120px' }}>UNIT PRICE</th>
              {!isPO && (
                <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#111827', borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB', width: '70px' }}>TAX</th>
              )}
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#111827', borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB', width: '130px' }}>AMOUNT (IDR)</th>
            </tr>
          </thead>
          <tbody>
            {(transaction.items || []).map((item, idx) => {
              const product = products.find((p) => p.id === item.productId);
              const description = product?.description || '';
              return (
                <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6', verticalAlign: 'top' }}>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#6B7280' }}>{idx + 1}</td>
                  <td style={{ padding: '12px', color: '#1F2937' }}>
                    <div style={{ fontWeight: 600 }}>{item.name}</div>
                    {description && (
                      <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '4px', whiteSpace: 'pre-line' }}>
                        {description}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {item.qty} {item.unit || ''}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{formatNumber(item.price)}</td>
                  {!isPO && (
                    <td style={{ padding: '12px', textAlign: 'center' }}>-</td>
                  )}
                  <td style={{ padding: '12px', textAlign: 'right', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600 }}>{formatNumber(item.total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Totals (invoice + quote only) */}
      {!isSJ && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '32px' }}>
          <div style={{ width: '300px', fontSize: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
              <span style={{ fontWeight: 600, color: '#374151' }}>Sub Total</span>
              <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{formatNumber(subTotal)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
              <span style={{ fontWeight: 600, color: '#374151' }}>Total</span>
              <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{formatNumber(total)}</span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 0',
                borderTop: '2px solid #111827',
                marginTop: '4px',
              }}
            >
              <span style={{ fontWeight: 700, color: '#111827' }}>
                {isInvoice ? 'Total Due' : 'Grand Total'}
              </span>
              <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, color: '#111827' }}>
                {formatNumber(isInvoice ? totalDue : total)} IDR
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Payment Details (invoice only) + Terms (invoice / quote / PO) */}
      {!isSJ && (() => {
        // For Quotation/PO: only show terms if user explicitly filled them on the transaction.
        // For Invoice: fall back to global settings.invoiceTerms when transaction.terms is empty.
        const termsText = isInvoice
          ? ((transaction?.terms || settings?.invoiceTerms || '').trim())
          : (transaction?.terms || '').trim();
        const showTerms = !!termsText;
        // Hide whole block if neither side has content
        const bankAccounts = (settings?.bankAccounts && settings.bankAccounts.length > 0)
          ? settings.bankAccounts
          : ((settings?.bankName || settings?.bankAccount || settings?.bankAccountName)
              ? [{ bankName: settings?.bankName, bankAccount: settings?.bankAccount, bankAccountName: settings?.bankAccountName }]
              : []);
        const showPayment = isInvoice && (settings?.paymentMethod || bankAccounts.length > 0);
        if (!showTerms && !showPayment) return null;
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', marginTop: '8px' }}>
            {showPayment ? (
              <div>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>Payment Details</div>
                <div style={{ fontSize: '11px', color: '#374151', lineHeight: 1.7 }}>
                  {settings?.paymentMethod && (
                    <div>
                      <span style={{ fontWeight: 600 }}>Method: </span>
                      <span>{settings.paymentMethod}</span>
                    </div>
                  )}
                  {bankAccounts.map((acc, i) => (
                    (acc.bankName || acc.bankAccount || acc.bankAccountName) ? (
                      <div key={i} style={{ marginTop: i === 0 ? 4 : 8 }}>
                        {bankAccounts.length > 1 && (
                          <div style={{ fontWeight: 600, color: '#111827' }}>Rekening #{i + 1}</div>
                        )}
                        {acc.bankName && <div>Rekening {acc.bankName}</div>}
                        {acc.bankAccount && <div>No. {acc.bankAccount}</div>}
                        {acc.bankAccountName && <div>An. {acc.bankAccountName}</div>}
                      </div>
                    ) : null
                  ))}
                </div>
              </div>
            ) : (
              <div />
            )}
            <div>
              {showTerms && (
                <>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>
                    {isQuote ? 'Notes & Validity' : isPO ? 'Terms & Notes' : 'Terms and Conditions'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                    {termsText}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Surat Jalan: Penerima / Pengirim signature */}
      {isSJ && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginTop: '60px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#111827', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '60px' }}>
              Penerima
            </div>
            <div style={{ borderTop: '1px solid #9CA3AF', margin: '0 32px', paddingTop: '6px' }}>
              <div style={{ fontSize: '11px', color: '#6B7280' }}>Nama & Tanda Tangan</div>
              <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '8px' }}>Tanggal: ______________</div>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#111827', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '60px' }}>
              Pengirim
            </div>
            <div style={{ borderTop: '1px solid #9CA3AF', margin: '0 32px', paddingTop: '6px' }}>
              <div style={{ fontSize: '11px', color: '#6B7280' }}>Nama & Tanda Tangan</div>
              <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '8px' }}>Tanggal: ______________</div>
            </div>
          </div>
        </div>
      )}

      {/* Notes */}
      {transaction.notes && (
        <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #E5E7EB' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>Notes</div>
          <div style={{ fontSize: '11px', color: '#374151', whiteSpace: 'pre-line' }}>{transaction.notes}</div>
        </div>
      )}
    </div>
  );
});

InvoiceTemplate.displayName = 'InvoiceTemplate';

export default InvoiceTemplate;
